// Modal "Länder sortieren" hinter dem Stift-Symbol im Drawer (IMG_4924,
// 01.09.2026): je Land ein Schalter fuer die Drawer-Liste, einer fuer die
// globale Suche, dazu die Reihenfolge.
//
// Reihenfolge per Auf/Ab statt Ziehgriff: Natives HTML5-Drag-and-drop
// funktioniert auf Touch-Geraeten nicht zuverlaessig, und eine eigene
// Zeigegeraet-Steuerung ist fuer eine Kleinigkeit wie diese nicht wert,
// eine neue Abhaengigkeit zu vermeiden. Siehe Plan zu Phase 3.

import { Eye, EyeOff, Search, SearchX, ChevronUp, ChevronDown, X } from "lucide-react";
import type { Land } from "../lib/bundle.js";
import type { LandSichtbarkeit } from "../lib/einstellungen.js";

export function LaenderSortieren({
  offen,
  onClose,
  laender,
  reihenfolge,
  sichtbarkeit,
  onReihenfolgeAendern,
  onSichtbarkeitAendern,
}: {
  offen: boolean;
  onClose: () => void;
  laender: Land[];
  reihenfolge: string[];
  sichtbarkeit: Record<string, LandSichtbarkeit>;
  onReihenfolgeAendern: (neu: string[]) => void;
  onSichtbarkeitAendern: (iso2: string, naechste: LandSichtbarkeit) => void;
}) {
  if (!offen) return null;

  const landByIso2 = new Map(laender.map((l) => [l.iso2, l]));
  const sortiert = reihenfolge.map((iso2) => landByIso2.get(iso2)).filter((l): l is Land => l !== undefined);

  function verschieben(index: number, richtung: -1 | 1) {
    const ziel = index + richtung;
    if (ziel < 0 || ziel >= reihenfolge.length) return;
    const naechste = [...reihenfolge];
    [naechste[index], naechste[ziel]] = [naechste[ziel]!, naechste[index]!];
    onReihenfolgeAendern(naechste);
  }

  function sicht(iso2: string): LandSichtbarkeit {
    return sichtbarkeit[iso2] ?? { list: true, search: true };
  }

  return (
    <div className="fixed inset-0 z-30 flex items-end justify-center bg-black/40 sm:items-center">
      <div className="max-h-[80vh] w-full max-w-md overflow-y-auto rounded-t-lg bg-flaeche-inhalt sm:rounded-lg">
        <div className="flex items-center justify-between border-b border-neutral-200 px-4 py-3">
          <h2 className="text-lg font-medium">Länder sortieren</h2>
          <button type="button" onClick={onClose} aria-label="Schließen" className="min-h-11 min-w-11">
            <X size={22} />
          </button>
        </div>

        <div className="flex items-center gap-6 px-4 py-2 text-xs text-neutral-500">
          <span className="flex items-center gap-1">
            <Eye size={16} /> In Länderliste
          </span>
          <span className="flex items-center gap-1">
            <Search size={16} /> In globaler Suche
          </span>
        </div>

        <ul>
          {sortiert.map((land, index) => {
            const zustand = sicht(land.iso2);
            return (
              <li key={land.iso2} className="flex items-center justify-between border-t border-neutral-100 px-4 py-2">
                <span className="text-sm">{land.nameDe}</span>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => onSichtbarkeitAendern(land.iso2, { ...zustand, list: !zustand.list })}
                    aria-label={zustand.list ? "Aus der Länderliste ausblenden" : "In der Länderliste zeigen"}
                    className="min-h-11 min-w-11 text-akzent"
                  >
                    {zustand.list ? <Eye size={20} /> : <EyeOff size={20} className="text-neutral-400" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => onSichtbarkeitAendern(land.iso2, { ...zustand, search: !zustand.search })}
                    aria-label={zustand.search ? "Aus der globalen Suche ausschließen" : "In der globalen Suche einschließen"}
                    className="min-h-11 min-w-11 text-fortschritt"
                  >
                    {zustand.search ? <Search size={20} /> : <SearchX size={20} className="text-neutral-400" />}
                  </button>
                  <div className="flex flex-col">
                    <button
                      type="button"
                      onClick={() => verschieben(index, -1)}
                      disabled={index === 0}
                      aria-label={`${land.nameDe} nach oben`}
                      className="flex h-5 items-center text-neutral-500 disabled:opacity-30"
                    >
                      <ChevronUp size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => verschieben(index, 1)}
                      disabled={index === sortiert.length - 1}
                      aria-label={`${land.nameDe} nach unten`}
                      className="flex h-5 items-center text-neutral-500 disabled:opacity-30"
                    >
                      <ChevronDown size={16} />
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>

        <div className="p-4">
          <button
            type="button"
            onClick={onClose}
            className="min-h-11 w-full rounded bg-akzent px-4 py-2 text-sm font-medium text-white hover:bg-akzent-dunkel"
          >
            Fertig
          </button>
        </div>
      </div>
    </div>
  );
}
